import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  AlertColor,
  Tabs,
  Tab,
  Paper,
  Grid2,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Chip,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Meal, Order, mealService, userService, Shift } from '../services';
import DietaryLabel from './DietaryLabel';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import ErrorBoundary from './ErrorBoundary';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`meal-tabpanel-${index}`}
    aria-labelledby={`meal-tab-${index}`}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const MealList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [meals, setMeals] = useState<Meal[]>([]);
  const [businessDays, setBusinessDays] = useState<{ date: string; dayName: string }[]>([]);
  const [selectedMeals, setSelectedMeals] = useState<{ [key: string]: number }>({});
  const [existingPreOrders, setExistingPreOrders] = useState<{ [key: string]: number }>({});
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mealsByDate, setMealsByDate] = useState<{ [key: string]: Meal[] }>({});
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [selectedShifts, setSelectedShifts] = useState<{ [key: string]: number }>({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get menus first to retrieve the active menu ID
        const menus = await userService.getOrganisationMenus();
        const activeMenu = menus.length > 0 ? menus[0] : null;
        
        if (!activeMenu) {
          throw new Error('No active menu found');
        }
        
        const menuId = activeMenu.id;
        
        const [mealsData, nextBusinessDays, mealsByDateData, existingOrders, shiftsData] = await Promise.all([
          mealService.getMeals(),
          mealService.getNextBusinessDays(5),
          mealService.getMealsByDate(5),
          mealService.getOrders(),
          userService.getShiftsForMenu(menuId) // Use userService with menuId instead
        ]);
        
        setMeals(mealsData);
        setBusinessDays(nextBusinessDays);
        setMealsByDate(mealsByDateData);
        setShifts(shiftsData);
        
        console.log("nextBusinessDays:", nextBusinessDays);
        console.log("mealsByDateData:", mealsByDateData);
        console.log("shifts:", shiftsData);
        console.log("activeMenu:", activeMenu);
        
        // For empty pre-orders
        const emptyPreOrders = {};
        setExistingPreOrders(emptyPreOrders);
  
        const initialSelectedMeals: { [key: string]: number } = {};
        const initialSelectedShifts: { [key: string]: number } = {};
        
        nextBusinessDays.forEach(day => {
          initialSelectedMeals[day.date] = existingOrders?.find(o => o?.deliveryDate?.startsWith(day.date))?.mealId ?? -1; // No meals selected initially
          initialSelectedShifts[day.date] = -1; // No shifts selected initially
        });
        
        setSelectedMeals(initialSelectedMeals);
        setSelectedShifts(initialSelectedShifts);

      } catch (error) {
        console.error('Failed to fetch data:', error);
        setError(t('errorLoadingData'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [t]);

  const handleMealSelection = (day: string, mealId: number) => {
    if (!existingPreOrders[day]) {
      setSelectedMeals(prev => ({ ...prev, [day]: mealId }));
    }
  };

  const handleShiftSelection = (day: string, shiftId: number) => {
    if (!existingPreOrders[day]) {
      setSelectedShifts(prev => ({ ...prev, [day]: shiftId }));
    }
  };

  const handlePreOrder = async () => {
    try {
      const orders: Omit<Order, 'id'>[] = businessDays
        .filter(day => 
          !existingPreOrders[day.date] && 
          selectedMeals[day.date] !== -1 && 
          selectedShifts[day.date] !== -1
        )
        .map((day) => ({
          date: day.date,
          mealId: selectedMeals[day.date],
          shiftId: selectedShifts[day.date] // Include selected shift in order
        }));

      console.log('Orders to be placed:', orders); // Debug log
      await mealService.preOrderMeals(orders);
      setSnackbar({
        open: true,
        message: t('placedsuccess'),
        severity: 'success'
      });
      navigate('/summary');
    } catch (error) {
      console.error('Failed to place pre-orders:', error);
      setSnackbar({
        open: true,
        message: t('errorPlacingOrder'),
        severity: 'error'
      });
    }
  };

  const renderMealCard = (meal: Meal, selected: boolean, onSelect: () => void, disabled: boolean) => (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        opacity: selected ? 1 : 0.9,
        transition: 'all 0.3s ease',
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: selected ? `2px solid ${theme.palette.primary.main}` : 'none',
        '&:hover': {
          transform: disabled ? 'none' : 'translateY(-4px)',
          boxShadow: disabled ? 1 : 4
        }
      }}
      onClick={() => !disabled && onSelect()}
    >
      <CardMedia
        component="img"
        height="140"
        image={meal.imagePath}
        alt={meal.name}
      />
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {meal.name}
        </Typography>
        <DietaryLabel dietaryInfo={meal.dietaryInfo} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {meal.description}
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selected && (
            <Chip
              label={t('selected')}
              color="primary"
              size="small"
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <ErrorBoundary>
      <Box sx={{ width: '100%' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t("preorder")}
        </Typography>
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePreOrder}
            disabled={
              Object.values(selectedMeals).every(mealId => mealId === -1 || existingPreOrders[mealId]) ||
              Object.values(selectedShifts).every(shiftId => shiftId === -1) ||
              businessDays.some(day => 
                selectedMeals[day.date] !== -1 && selectedShifts[day.date] === -1
              )
            }
            size="large"
          >
            {t("placepreorders")}
          </Button>
        </Box>
        <Paper sx={{ width: '100%', mt: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant={isMobile ? "scrollable" : "fullWidth"}
            scrollButtons="auto"
            aria-label="meal ordering tabs"
          >
            {businessDays.map((day, index) => (
              <Tab
                key={day.date}
                label={
                  <Box>
                    <Typography variant="subtitle2">
                      {t(day.dayName)}
                    </Typography>
                    <Typography variant="caption">
                      {format(new Date(day.date), "d.M.")}
                    </Typography>
                  </Box>
                }
                id={`meal-tab-${index}`}
                aria-controls={`meal-tabpanel-${index}`}
                disabled={!!existingPreOrders[day.date]}
              />
            ))}
          </Tabs>

          {businessDays.map((day, index) => (
            <TabPanel key={day.date} value={activeTab} index={index}>
              {existingPreOrders[day.date] ? (
                <Alert severity="info">
                  {t('preorderset')} <Button color="primary" onClick={() => navigate('/summary')}>{t('summary')}</Button>
                </Alert>
              ) : (
                <>
                  <Box sx={{ mb: 3 }}>
                    <FormControl fullWidth>
                      <InputLabel id={`shift-select-label-${day.date}`}>{t('selectShift')}</InputLabel>
                      <Select
                        labelId={`shift-select-label-${day.date}`}
                        id={`shift-select-${day.date}`}
                        value={selectedShifts[day.date]}
                        label={t('selectShift')}
                        onChange={(e) => handleShiftSelection(day.date, e.target.value as number)}
                      >
                        <MenuItem value={-1}>{t('selectShift')}</MenuItem>
                        {shifts.map((shift) => (
                          <MenuItem key={shift.id} value={shift.id}>
                            {shift.name} - {typeof shift.orderDeliveryTime === 'string' 
                              ? format(new Date(`2000-01-01T${shift.orderDeliveryTime}`), 'HH:mm')
                              : format(new Date(shift.orderDeliveryTime), 'HH:mm')}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{selectedShifts[day.date] === -1 && selectedMeals[day.date] !== -1 ? t('pleaseSelectShift') : ''}</FormHelperText>
                    </FormControl>
                  </Box>
                  
                  <Grid2 container spacing={3}>
                    {/* Use mealsByDate to get meals for this specific day */}
                    {(mealsByDate[day.date] || []).map((meal) => (
                      <Grid2 key={meal.id} size={{ xs: 12, sm: 6, md: 4 }}>
                        {renderMealCard(
                          meal,
                          selectedMeals[day.date] === meal.id,
                          () => handleMealSelection(day.date, meal.id),
                          !!existingPreOrders[day.date]
                        )}
                      </Grid2>
                    ))}
                  </Grid2>
                </>
              )}
            </TabPanel>
          ))}
        </Paper>



        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ErrorBoundary>
  );
};

export default MealList;